import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLightbulb, faChevronRight, faCalendar, faStar, faUsers,
  faWineGlass, faFire, faTorah, faGift, faScroll
} from '@fortawesome/free-solid-svg-icons';
import { PRIMARY_COLOR, PRIMARY_COLOR_RGBA_20 } from '../../constants/colors';
import { getExperienceIcon } from './ExperienceTimelineCard';

export default function SuggestionsSection({ suggestions, onCreateNew }) {
  const navigate = useNavigate();
  const location = useLocation();

  if (suggestions.length === 0) return null;

  return (
    <section className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-3xl p-6 md:p-8 border-2 border-primary/30">
      <div className="flex items-center gap-3 mb-6">
        <FontAwesomeIcon icon={faLightbulb} className="text-3xl md:text-4xl" style={{ color: PRIMARY_COLOR }} />
        <div>
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-text-primary">
            Suggestions for You
          </h2>
          <p className="text-text-secondary">
            Meaningful experiences to consider
          </p>
        </div>
      </div>

      {/* Desktop: Grid */}
      <div className="hidden md:grid grid-cols-3 gap-4">
        {suggestions.map((suggestion, index) => (
          <SuggestionCard
            key={suggestion.type}
            suggestion={suggestion}
            index={index}
            onSelect={() => {
              navigate('/', { state: { openCreateModal: true, type: suggestion.type } });
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('createExperience', { detail: { type: suggestion.type } }));
              }, 500);
            }}
          />
        ))}
      </div>

      {/* Mobile: Horizontal Scroll */}
      <div className="md:hidden overflow-x-auto -mx-6 px-6 no-scrollbar">
        <div className="flex gap-4 min-w-max">
          {suggestions.map((suggestion, index) => (
            <SuggestionCard
              key={suggestion.type}
              suggestion={suggestion}
              index={index}
              onSelect={() => {
              navigate('/', { state: { openCreateModal: true, type: suggestion.type } });
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('createExperience', { detail: { type: suggestion.type } }));
              }, 500);
            }}
              mobile={true}
            />
          ))}
        </div>
      </div>

      {/* View All Button */}
      <div className="mt-6 text-center">
        <button
          onClick={onCreateNew}
          className="text-primary hover:text-primary-dark font-semibold flex items-center gap-2 mx-auto touch-target"
        >
          <span>Browse all experience types</span>
          <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

function SuggestionCard({ suggestion, index, onSelect, mobile = false }) {
  const IconComponent = getExperienceIcon(suggestion.icon);

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onSelect}
      className={`
        bg-white rounded-2xl border-2 border-transparent hover:border-primary/50 p-5 transition-all text-left touch-target
        ${mobile ? 'w-72' : ''}
      `}
    >
      <div className="flex flex-col">
        <div className="text-3xl md:text-4xl mb-3 text-primary">
          <FontAwesomeIcon icon={IconComponent} />
        </div>
        <h3 className="font-serif font-bold text-lg text-text-primary mb-2">
          {suggestion.title}
        </h3>
        <p className="text-sm text-text-secondary mb-4">
          {suggestion.description}
        </p>
        
        {/* Reason badge */}
        <div className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full" style={{ color: PRIMARY_COLOR, backgroundColor: PRIMARY_COLOR_RGBA_20 }}>
        {suggestion.reason === 'upcoming-holiday' && (
          <>
            <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" />
            Upcoming
          </>
        )}
        {suggestion.reason === 'tradition' && (
          <>
            <FontAwesomeIcon icon={faStar} className="w-3 h-3" />
            Tradition
          </>
        )}
        {suggestion.reason === 'age-appropriate' && (
          <>
            <FontAwesomeIcon icon={faUsers} className="w-3 h-3" />
            Perfect timing
          </>
        )}
        </div>
      </div>
    </motion.button>
  );
}

